import {
  DateRangePickerField,
  Form,
  NumericFormatField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ExpenditureBudgetJurisdictionFormSchema } from '../../models';
import { EXPENDITURE_BUDGET_JURISDICTION_CODE_LENGTH } from '../../utils';
import { getCodeMaxValue } from '../../utils/get-code-max-value';

type FormValues = z.infer<typeof ExpenditureBudgetJurisdictionFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const ExpenditureBudgetJurisdictionForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ExpenditureBudgetJurisdictionFormSchema),
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        errors={errors}
        disableSeparator
        max={getCodeMaxValue(EXPENDITURE_BUDGET_JURISDICTION_CODE_LENGTH)}
      />
      <TextInputField
        name="name"
        autocompleteKey="expenditureBudgetJurisdiction.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />
      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />
      <NumericFormatField
        name="sortOrder"
        label={t('display-sort-order')}
        control={control}
        errors={errors}
        required
        allowNegative={false}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
